import { request } from '@/functions/data-access.ts';
import useHintModals from '@/hooks/useHintModals.tsx';
import { getErrorMessage } from '@/shared/utils/error';
import { faBuilding, faLock } from '@fortawesome/sharp-solid-svg-icons';
import { FormString } from '@lib/sc-eone-ui';
import { Labels } from '@lib/sc-eone-ui/src/strings/strings-en-us';
import { FormSelect } from 'eone-ui/forms/form-select';
import { getDataTestId } from 'eone-ui/functions/data-test-ids';
import { Modal, ModalForm } from 'eone-ui/modal/modal';
import { ModalButtonType } from 'eone-ui/modal/modal-constants';
import { useEffect, useMemo, useState } from 'react';

function SwitchAccountModalError(props) {
  if (!props.Message) {
    return null;
  }
  return <label className="pd-error-label">{props.Message}</label>;
}

interface AccountSwitchModel {
  customerId: string;
  userId: string;
  uamUserId: string;
  regionId: string;
  switchRegion: boolean;
  accountName: string;
  accountId: string;
  redirectUri: string;
  mfaEnabled: true;
}

function SwitchAccountModal(props) {
  const [accounts, setAccounts] = useState<AccountSwitchModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [customerId, setCustomerId] = useState(null);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const { showErrorModal } = useHintModals();

  useEffect(() => {
    setCustomerId(null);
    setCode('');
    setPassword('');
    setError('');
    if (accounts.length === 0) {
      setAccounts([]);
      setLoading(true);
      loadAccounts();
    }
  }, []);

  const loadAccounts = async () => {
    try {
      const accountList = await request({ url: '/account/get-accounts', method: 'GET' });
      setAccounts(accountList);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setAccounts([]);
      await showErrorModal({
        Title: 'Account Switch Error',
        Error: {
          description: getErrorMessage(error),
        },
      });
    }
  };

  const switchAccount = async () => {
    const body: { CustomerId: string | null; Code?: string; Password?: string } = {
      CustomerId: customerId,
    };
    if (requiresCode()) {
      body.Code = code;
    }
    if (requiresPassword()) {
      body.Password = password;
    }
    try {
      const redirectUri = await request({
        url: '/account/switch-account',
        method: 'POST',
        data: body,
      });
      window.location.href = redirectUri;
    } catch (error) {
      setError(error instanceof Error ? error.message : String(error));
    }
  };

  const changeCode = async (value) => {
    setCode(value);
  };
  const changePassword = async (value) => {
    setPassword(value);
  };

  const requiresCode = () => {
    for (let i = 0, len = accounts.length; i < len; i++) {
      if (accounts[i].customerId === customerId) {
        return accounts[i].mfaEnabled;
      }
    }
    return false;
  };
  const requiresPassword = () => {
    for (let i = 0, len = accounts.length; i < len; i++) {
      if (accounts[i].customerId === customerId) {
        return accounts[i].switchRegion;
      }
    }
    return false;
  };

  const changeAccount = async (value) => {
    setCustomerId(value);
    setCode('');
    setPassword('');
  };

  const getOptions = useMemo(() => {
    if (!accounts?.length) return [];
    const options = accounts.map((account) => ({
      Key: account.customerId,
      Value: account.accountName,
      Icon: account.mfaEnabled ? faLock : faBuilding,
    }));
    return options;
  }, [accounts]);

  const getButtons = () => {
    return [
      {
        Label: Labels.Switch,
        Type: ModalButtonType.Primary,
        Visible:
          customerId &&
          (!requiresCode() || code.length === 6) &&
          (!requiresPassword() || password.length > 0),
        OnClick: switchAccount,
      },
      {
        Label: Labels.Cancel,
        Type: ModalButtonType.Secondary,
        Visible: true,
        OnClick: props.OnCancel,
      },
    ];
  };

  return (
    <Modal
      Title={Labels.SwitchAccount}
      Show
      Close={props.OnCancel}
      FooterButtons={getButtons()}
      DataTestId={props.DataTestId}
    >
      <ModalForm>
        <FormSelect
          Label={Labels.Account}
          Loading={loading}
          Options={getOptions}
          Placeholder={Labels.SelectAccount}
          Search
          Value={customerId}
          OnChange={changeAccount}
          DataTestId={getDataTestId(props.DataTestId, 'account')}
        />
        <FormString
          Label={Labels.Password}
          Visible={requiresPassword()}
          Value={password}
          OnChange={changePassword}
          Password
          DataTestId={getDataTestId(props.DataTestId, 'password')}
        />
        <FormString
          Label={Labels.Mfa}
          MaxLength={6}
          Visible={requiresCode()}
          ValidChars={['0', '1', '2', '3', '4', '5', '6', '7', '8', '9']}
          Value={code}
          OnChange={changeCode}
          DataTestId={getDataTestId(props.DataTestId, 'mfa-code')}
        />
        <SwitchAccountModalError Message={error} />
      </ModalForm>
    </Modal>
  );
}

export { SwitchAccountModal };

//import { SCComponentRoot } from '@lib/sc-eone-ui';
//import { Button } from 'eone-ui/buttons/button';
//import { FormSelect } from 'eone-ui/forms/form-select';
//import { FormString } from 'eone-ui/forms/form-string';
//import { Modal, ModalForm } from 'eone-ui/modal/modal';
//import { ModalButtonType } from 'eone-ui/modal/modal-constants';
//import { useState } from 'react';

//function showModal({ Region }) {
//  SCComponentRoot.renderComponent(
//    'switchAccountModal',
//    <SwitchAccountModal
//      Show
//      Close={() => {
//        SCComponentRoot.renderComponent('switchAccountModal', null);
//      }}
//      Add={() => {
//        // TODO
//      }}
//      Region={Region}
//    />,
//  );
//}

//function SwitchAccount(props) {
//  return (
//    <span className="sc-app-header-account">
//      <Button
//        Label={props.AccountName}
//        Title={props.MultipleAccounts ? 'Switch account' : props.AccountName}
//        OnClick={() => {
//          if (props.MultipleAccounts) {
//            showModal({ Region: props.Region });
//          }
//        }}
//      />
//    </span>
//  );
//}

//function SwitchAccountModal(props) {
//  const [newAccount, setNewAccount] = useState(null);
//  const [accountList] = useState([]);
//  const [accountOptions] = useState(null);
//  const [mfaCode, setMfaCode] = useState('');
//  const [showMfa, setShowMfa] = useState(false);
//  const [password, setPassword] = useState('');
//  const [showPassword, setShowPassword] = useState(false);

//  const requiredFields = () => {
//    if (newAccount == null) return false;
//    if (showPassword && password == '') return false;
//    if (showMfa && mfaCode == '') return false;
//    return true;
//  };
//  const selectNewAccount = (value) => {
//    setNewAccount(value);
//    const sel: any = accountList.find((obj: any) => {
//      return obj.accountProductId == value;
//    });
//    setShowMfa(sel.mfaEnabled);
//    setShowPassword(props.Region != sel.regionId);
//  };
//  const switchAccount = () => {
//    const sel = accountList.find((obj: any) => {
//      return obj.accountProductId == newAccount;
//    });
//    const body = {
//      UserProduct: sel,
//      MfaCode: mfaCode,
//      Password: password,
//    };
//    props.Add(body);
//  };
//  const getFooterButtons = () => {
//    return [
//      {
//        Label: 'Switch',
//        Type: ModalButtonType.Primary,
//        Visible: requiredFields(),
//        OnClick: switchAccount(),
//      },
//      {
//        Label: 'Cancel',
//        Type: ModalButtonType.Secondary,
//        Visible: true,
//        OnClick: props.Close,
//      },
//    ];
//  };
//  return (
//    <Modal
//      Title={Labels.SwitchAccount}
//      Show={props.Show}
//      Close={props.Close}
//      FooterButtons={getFooterButtons()}
//    >
//      <ModalForm>
//        <FormSelect
//          Search
//          Label="Account"
//          Placeholder="Select account"
//          Options={accountOptions}
//          Value={newAccount}
//          OnChange={selectNewAccount}
//        />
//        <FormString
//          Label="MFA code"
//          Placeholder="Enter MFA code"
//          Value={mfaCode}
//          Visible={showMfa}
//          OnChange={setMfaCode}
//        />
//        <FormString
//          Label="Password"
//          Placeholder="Enter password"
//          Password
//          Value={password}
//          Visible={showPassword}
//          OnChange={setPassword}
//        />
//      </ModalForm>
//    </Modal>
//  );
//}

//export { SwitchAccount };
